import { CostCalculationExternal } from 'generated';
import { useTranslation } from 'react-i18next';

import { Grid } from '@mui/material';

import HoursDisplay from 'components/shared/cell-formatters/HoursDisplay';
import MonetaryDisplay from 'components/shared/cell-formatters/MonetaryDisplay';
import PercentageDisplay from 'components/shared/cell-formatters/PercentageDisplay';

type CostCalculationExternalMonthProperties = {
  record: CostCalculationExternal;
};

export default function CostCalculationExternalMonth(
  properties: CostCalculationExternalMonthProperties
) {
  const monthRecord = properties.record;
  const { t } = useTranslation('common');

  return (
    <>
      <Grid
        container
        display="flex"
        alignItems="start"
        mt={2}
        columnSpacing={2}
        style={{
          fontFamily: "'signikaregular', Arial, Helvetica, 'sans-serif'",
          backgroundColor: '#EEEEEE'
        }}
      >
        <Grid item xs={2}>
          {t('description')}
        </Grid>
        <Grid item xs={1} style={{ textAlign: 'right' }}>
          {t('hours')}
        </Grid>
        <Grid item xs={1} style={{ textAlign: 'right' }}>
          {t('rate')}
        </Grid>
        <Grid item xs={1} style={{ textAlign: 'right' }}>
          {t('percentage')}
        </Grid>
        <Grid item xs={1} style={{ textAlign: 'right' }}>
          {t('total')}
        </Grid>
      </Grid>
      {monthRecord.hours.map((deployedHours, index) => (
        <Grid
          container
          key={index}
          display="flex"
          alignItems="start"
          columnSpacing={2}
        >
          <Grid item xs={2}>
            {deployedHours.description}
          </Grid>
          <Grid item xs={1}>
            <HoursDisplay value={deployedHours.hours} />
          </Grid>
          <Grid item xs={1}>
            <MonetaryDisplay value={deployedHours.baseRate} />
          </Grid>
          <Grid item xs={1}>
            <PercentageDisplay value={deployedHours.percentage} />
          </Grid>
          <Grid item xs={1}>
            <MonetaryDisplay value={deployedHours.totalHours} />
          </Grid>
        </Grid>
      ))}
      <Grid
        container
        display="flex"
        alignItems="start"
        columnSpacing={2}
        style={{
          fontFamily: "'signikaregular', Arial, Helvetica, 'sans-serif'"
        }}
      >
        <Grid item xs={2}>
          {t('hoursTotal')}
        </Grid>
        <Grid item xs={3} />
        <Grid item xs={1} borderTop={1} textAlign="right">
          &#x21B3;
        </Grid>
        <Grid item xs={1}>
          <MonetaryDisplay value={monthRecord.totalHours} />
        </Grid>
      </Grid>

      <Grid
        container
        display="flex"
        alignItems="start"
        mt={1}
        columnSpacing={2}
        style={{
          fontFamily: "'signikaregular', Arial, Helvetica, 'sans-serif'",
          backgroundColor: '#EEEEEE'
        }}
      >
        <Grid item xs={2}>
          {t('fee')}
        </Grid>
        <Grid item xs={1} textAlign="right">
          {t('ofTotal')}
        </Grid>
        <Grid item xs={2} />
        <Grid item xs={1} textAlign="right">
          {t('total')}
        </Grid>
      </Grid>
      {monthRecord.fees.map((fee, index) => (
        <Grid
          container
          key={index}
          display="flex"
          alignItems="start"
          columnSpacing={2}
        >
          <Grid item xs={2}>
            <PercentageDisplay value={fee.feePercentage} align="left" />
          </Grid>
          <Grid item xs={1}>
            <MonetaryDisplay value={fee.feeOfTotal} />
          </Grid>
          <Grid item xs={2} />
          <Grid item xs={1}>
            <MonetaryDisplay value={fee.total} />
          </Grid>
        </Grid>
      ))}
      <Grid
        container
        display="flex"
        alignItems="start"
        columnSpacing={2}
        style={{
          fontFamily: "'signikaregular', Arial, Helvetica, 'sans-serif'"
        }}
      >
        <Grid item xs={2}>
          {t('feeTotal')}
        </Grid>
        <Grid item xs={3} />
        <Grid item xs={1} borderTop={1} textAlign="right">
          &#x21B3;
        </Grid>
        <Grid item xs={1}>
          <MonetaryDisplay value={monthRecord.totalFees} />
        </Grid>
      </Grid>

      <Grid
        container
        display="flex"
        alignItems="start"
        mt={1}
        columnSpacing={2}
        style={{
          fontFamily: "'signikaregular', Arial, Helvetica, 'sans-serif'",
          backgroundColor: '#EEEEEE'
        }}
      >
        <Grid item xs={2}>
          {t('travelExpenses')}
        </Grid>
        <Grid item xs={1} textAlign="right">
          {t('km')}
        </Grid>
        <Grid item xs={2} />
        <Grid item xs={1} textAlign="right">
          {t('total')}
        </Grid>
      </Grid>
      <Grid container display="flex" alignItems="start" columnSpacing={2}>
        <Grid item xs={2} />
        <Grid item xs={1}>
          <HoursDisplay value={monthRecord.totalKm} />
        </Grid>
        <Grid item xs={2} />
        <Grid item xs={1}>
          <MonetaryDisplay value={monthRecord.totalTravelExpenses} />
        </Grid>
      </Grid>
      <Grid
        container
        display="flex"
        alignItems="start"
        columnSpacing={2}
        style={{
          fontFamily: "'signikaregular', Arial, Helvetica, 'sans-serif'"
        }}
      >
        <Grid item xs={2}>
          {t('travelExpensesTotal')}
        </Grid>
        <Grid item xs={3} />
        <Grid item xs={1} borderTop={1} textAlign="right">
          &#x21B3;
        </Grid>
        <Grid item xs={1}>
          <MonetaryDisplay value={monthRecord.totalTravelExpenses} />
        </Grid>
      </Grid>

      <Grid
        container
        display="flex"
        alignItems="start"
        mt={1}
        columnSpacing={2}
        style={{
          fontFamily: "'signikaregular', Arial, Helvetica, 'sans-serif'",
          backgroundColor: '#EEEEEE'
        }}
      >
        <Grid item xs={2}>
          {t('vat')}
        </Grid>
        <Grid item xs={1} textAlign="right">
          {t('ofTotal')}
        </Grid>
        <Grid item xs={2} />
        <Grid item xs={1} textAlign="right">
          {t('total')}
        </Grid>
      </Grid>
      {monthRecord.vat.map((v, index) => (
        <Grid
          container
          key={index}
          display="flex"
          alignItems="start"
          columnSpacing={2}
        >
          <Grid item xs={2}>
            <PercentageDisplay value={v.vatPercentage} align="left" />
          </Grid>
          <Grid item xs={1}>
            <MonetaryDisplay value={v.vatOfTotal} />
          </Grid>
          <Grid item xs={2} />
          <Grid item xs={1}>
            <MonetaryDisplay value={v.total} />
          </Grid>
        </Grid>
      ))}
      <Grid
        container
        display="flex"
        alignItems="start"
        columnSpacing={2}
        style={{
          fontFamily: "'signikaregular', Arial, Helvetica, 'sans-serif'"
        }}
      >
        <Grid item xs={2}>
          {t('vatTotal')}
        </Grid>
        <Grid item xs={3} />
        <Grid item xs={1} borderTop={1} textAlign="right">
          &#x21B3;
        </Grid>
        <Grid item xs={1} borderBottom={1}>
          <MonetaryDisplay value={monthRecord.totalVat} />
        </Grid>
      </Grid>

      <Grid
        container
        display="flex"
        alignItems="start"
        mb={1}
        mt={1}
        columnSpacing={2}
        style={{
          fontFamily: "'signikaregular', Arial, Helvetica, 'sans-serif'",
          backgroundColor: '#CCCCCC'
        }}
      >
        <Grid item xs={2}>
          {t('total')}
        </Grid>
        <Grid item xs={4} />
        <Grid item xs={1} borderBottom={2}>
          <MonetaryDisplay value={monthRecord.total} />
        </Grid>
      </Grid>
    </>
  );
}
